import { useCallback, useMemo } from "react";

export interface Use2DNavigationArgs {
    itemCount: number;
    columns: number;
    focusItem: (index: number) => void;
    isDisabledAt?: (index: number) => boolean;
}

/**
 * Hook for 2D keyboard navigation in multi-column lists
 * Implements WAI-ARIA Grid pattern for arrow key navigation
 */
export const use2DNavigation = ({ itemCount, columns, focusItem, isDisabledAt = () => false }: Use2DNavigationArgs) => {
    const rows = useMemo(() => Math.ceil(itemCount / columns), [itemCount, columns]);

    /**
     * Get row and column position from index
     */
    const getPosition = useCallback(
        (index: number) => ({
            row: Math.floor(index / columns),
            col: index % columns,
        }),
        [columns],
    );

    /**
     * Get index from row and column position
     */
    const getIndexFromPosition = useCallback(
        (row: number, col: number) => {
            const index = row * columns + col;
            return index >= 0 && index < itemCount ? index : -1;
        },
        [columns, itemCount],
    );

    /**
     * Find the next enabled cell in the given direction
     */
    const findNextInDirection = useCallback(
        (currentIndex: number, direction: "up" | "down" | "left" | "right"): number => {
            const { row, col } = getPosition(currentIndex);
            const nextRow = direction === "up" ? row - 1 : direction === "down" ? row + 1 : row;
            const nextCol = direction === "left" ? col - 1 : direction === "right" ? col + 1 : col;

            // Check boundaries
            if (nextRow < 0 || nextRow >= rows || nextCol < 0 || nextCol >= columns) {
                return -1;
            }

            const nextIndex = getIndexFromPosition(nextRow, nextCol);

            // Check if valid and enabled
            if (nextIndex < 0 || nextIndex >= itemCount || isDisabledAt(nextIndex)) {
                return -1;
            }

            return nextIndex;
        },
        [getPosition, getIndexFromPosition, rows, columns, itemCount, isDisabledAt],
    );

    /**
     * Handle arrow key navigation in 2D grid
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent, currentIndex: number): boolean => {
            const directionMap: Record<string, "up" | "down" | "left" | "right"> = {
                ArrowUp: "up",
                ArrowDown: "down",
                ArrowLeft: "left",
                ArrowRight: "right",
            };

            const direction = directionMap[e.key];
            if (!direction) {
                return false;
            }

            const nextIndex = findNextInDirection(currentIndex, direction);
            if (nextIndex >= 0) {
                e.preventDefault();
                focusItem(nextIndex);
                return true;
            }

            return false;
        },
        [findNextInDirection, focusItem],
    );

    return { rows, columns, handleKeyDown };
};
